import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, Check, Sparkles, CreditCard, MessageCircle, Loader2 } from 'lucide-react';
import { SEOHead } from '../hooks/usePageSEO';
import { AnimatedSection, SectionBadge, BackNav } from '../components/shared';
import { getParcoursBySlug, PARCOURS } from '../data/parcoursData';

/**
 * ParcoursPage — Page parcours générique (effet "Wahou").
 * Hero gradient + section badge + grille d'étapes (style MethodSection) + sections additionnelles.
 */
const ParcoursPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const parcours = getParcoursBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Map slug → option de paiement du tunnel unifié /transformation (compte + accès app
  // synchronisés automatiquement via webhook Stripe — jamais le PaymentModal générique ici,
  // qui est réservé au présentiel et ne crée/synchronise aucun accès app).
  const PAYMENT_OPTION_MAP = {
    'coaching-online': 'monthly',
    'coaching-hybride': 'vip_monthly',
  };
  const paymentCatId = PAYMENT_OPTION_MAP[slug] ? slug : null;

  const openPayment = () => navigate(`/transformation?autocheckout=${PAYMENT_OPTION_MAP[slug]}`);
  const [payLoading, setPayLoading] = useState(false);
  const handlePayClick = () => {
    if (payLoading) return;
    setPayLoading(true);
    setTimeout(openPayment, 350);
  };

  const hasPayment = Boolean(paymentCatId);

  if (!parcours) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Parcours introuvable</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-3 rounded-xl bg-primary text-white font-semibold"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const Icon = parcours.icon;
  const otherParcours = PARCOURS.filter((p) => p.slug !== parcours.slug).slice(0, 4);

  return (
    <>
      <SEOHead
        title={`Parcours ${parcours.label} | Body-Up Personal Training Nyon`}
        description={`${parcours.tagline} ${parcours.description.slice(0, 130)}`}
        path={`/parcours/${parcours.slug}`}
      />

      <div className="min-h-screen bg-black text-white" data-testid={`parcours-page-${parcours.slug}`}>
        {/* ========== HERO "WAHOU" ========== */}
        <section className="relative pt-24 sm:pt-28 pb-20 overflow-hidden">
          {/* Background glow */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl opacity-60"
            style={{ background: parcours.glowRgba }}
            aria-hidden="true"
          />
          {/* Grain overlay */}
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg viewBox=\"0 0 200 200\" xmlns=\"http://www.w3.org/2000/svg\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.9\"/></filter><rect width=\"100%\" height=\"100%\" filter=\"url(%23n)\"/></svg>')" }} />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              {/* Retour — navigation navigateur (revient exactement où l'utilisateur était avant) */}
              <BackNav label="Retour" />

              {/* Badge parcours */}
              <SectionBadge emoji={parcours.emoji}>
                {`PARCOURS — ${parcours.label.toUpperCase()}`}
              </SectionBadge>

              {/* Big icon */}
              <motion.div
                initial={{ scale: 0, rotate: -15 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
                className={`mx-auto mt-8 mb-6 w-24 h-24 rounded-3xl bg-gradient-to-br ${parcours.gradient} flex items-center justify-center shadow-2xl`}
                style={{ boxShadow: `0 20px 60px ${parcours.glowRgba}` }}
              >
                <Icon className="w-12 h-12 text-white" strokeWidth={2} />
              </motion.div>

              {/* Title with gradient */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span className="block text-white">{parcours.label}</span>
                <span className={`block text-transparent bg-clip-text bg-gradient-to-r ${parcours.gradient}`}>
                  {parcours.tagline}
                </span>
              </h1>

              <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
                {parcours.description}
              </p>

              {/* Quick info pills */}
              <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300">
                  <Clock className="w-4 h-4 text-zinc-400" />
                  <span>{parcours.duration}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-300">
                  <Calendar className="w-4 h-4 text-zinc-400" />
                  <span>{parcours.sessions}</span>
                </div>
                {parcours.price && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r ${parcours.gradient}`}>
                    <Sparkles className="w-4 h-4" />
                    <span>{parcours.price}</span>
                  </div>
                )}
                {parcours.badge && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-black tracking-wider">
                    <Sparkles className="w-3.5 h-3.5" />
                    {parcours.badge}
                  </div>
                )}
              </div>

              {/* Primary CTA */}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                {hasPayment ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate(`/postuler-coaching?type=${paymentCatId === 'coaching-hybride' ? 'hybride' : 'online'}`)}
                      data-testid="parcours-cta-postuler"
                      className={`group inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r ${parcours.gradient} text-white font-bold shadow-2xl hover:scale-105 transition-transform`}
                    >
                      <Sparkles className="w-5 h-5" />
                      Postuler pour un appel avec Laurent
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      type="button"
                      onClick={handlePayClick}
                      disabled={payLoading}
                      data-testid="parcours-cta-pay"
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl bg-white/5 border-2 border-white/15 hover:border-white/40 text-white font-semibold transition-all disabled:opacity-60"
                    >
                      {payLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                      Payer maintenant
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/contact" data-testid="parcours-cta-primary">
                      <button className={`group inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r ${parcours.gradient} text-white font-bold shadow-2xl hover:scale-105 transition-transform`}>
                        <Calendar className="w-5 h-5" />
                        {parcours.cta}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </Link>
                    <Link to="/tarifs" className="text-sm text-zinc-400 hover:text-white underline underline-offset-4">
                      Voir les tarifs détaillés
                    </Link>
                  </>
                )}
              </div>
              {hasPayment && (
                <p className="mt-4 text-xs text-zinc-500 max-w-lg mx-auto">
                  Diagnostic personnalisé + plan d'action clair. Sans engagement, sans blabla — mais je filtre les candidatures pour n'accompagner que les personnes réellement engagées.
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* ========== POUR QUI ? ========== */}
        <section className="py-16 bg-gradient-to-b from-black to-zinc-950">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900/60 to-black p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${parcours.gradient} flex items-center justify-center`}>
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Pour qui ce parcours ?
                  </h2>
                </div>
                <ul className="grid md:grid-cols-2 gap-3">
                  {parcours.forWho.map((line, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-300">
                      <span className={`shrink-0 w-2 h-2 rounded-full mt-2 bg-gradient-to-r ${parcours.gradient}`} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ========== ÉTAPES "WAHOU" ========== */}
        <section className="py-20 bg-zinc-950 relative overflow-hidden">
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-50" style={{ background: parcours.glowRgba }} />
          <div className="absolute top-1/2 right-0 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: parcours.glowRgba }} />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="text-center mb-16">
                <SectionBadge emoji="🧭">LES ÉTAPES DU PARCOURS</SectionBadge>
                <h2 className="text-3xl md:text-5xl font-bold text-white mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Comment on avance
                  <span className={`block text-transparent bg-clip-text bg-gradient-to-r ${parcours.gradient}`}>
                    {`ensemble en ${parcours.steps.length} étapes`}
                  </span>
                </h2>
              </div>
            </AnimatedSection>

            <div className={`grid gap-6 ${parcours.steps.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-5'}`}>
              {/* Connector line (desktop) */}
              <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" aria-hidden="true" />

              {parcours.steps.map((step, i) => {
                const StepIcon = step.icon;
                return (
                  <AnimatedSection key={i} delay={i * 120}>
                    <div className="group relative bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-2xl p-6 hover:-translate-y-1 transition-all duration-500 h-full">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="relative shrink-0">
                          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${parcours.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                            <StepIcon className="w-6 h-6 text-white" />
                          </div>
                          <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-black border-2 border-white/30 text-[10px] font-black text-white flex items-center justify-center">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {step.title}
                      </h3>
                      <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========== FINAL CTA ========== */}
        <section className="py-20 relative overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${parcours.gradient} opacity-15`} />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Prêt à démarrer ?
              </h2>
              <p className="text-zinc-300 text-lg mb-8 max-w-xl mx-auto">
                Réserve ta <strong className="text-white">consultation 100 % gratuite</strong> pour échanger avec ton futur coach et confirmer que ce parcours est fait pour toi.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {hasPayment ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate(`/postuler-coaching?type=${paymentCatId === 'coaching-hybride' ? 'hybride' : 'online'}`)}
                      data-testid="parcours-cta-final-postuler"
                      className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r ${parcours.gradient} text-white font-bold shadow-2xl hover:scale-105 transition-transform`}
                    >
                      <Sparkles className="w-5 h-5" />
                      Postuler pour un appel avec Laurent
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={handlePayClick}
                      disabled={payLoading}
                      data-testid="parcours-cta-final-pay"
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl bg-white/5 border-2 border-white/15 hover:border-white/40 text-white font-semibold transition-all disabled:opacity-60"
                    >
                      {payLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                      Payer maintenant
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/contact" data-testid="parcours-cta-final">
                      <button className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r ${parcours.gradient} text-white font-bold shadow-2xl hover:scale-105 transition-transform`}>
                        <Calendar className="w-5 h-5" />
                        Réserver ma consultation gratuite
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </Link>
                    <Link to="/tarifs" className="text-sm text-zinc-400 hover:text-white underline underline-offset-4">
                      Voir les tarifs
                    </Link>
                  </>
                )}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ========== AUTRES PARCOURS ========== */}
        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <h3 className="text-2xl font-bold text-white text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Découvrir les autres parcours
              </h3>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {otherParcours.map((p, i) => {
                const OtherIcon = p.icon;
                return (
                  <AnimatedSection key={p.slug} delay={i * 80}>
                    <Link
                      to={`/parcours/${p.slug}`}
                      className="group block rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-5 hover:-translate-y-1 hover:border-white/30 transition-all"
                      data-testid={`other-parcours-${p.slug}`}
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <OtherIcon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-base font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {p.label}
                      </h4>
                      <p className="text-xs text-zinc-400 leading-relaxed">{p.tagline}</p>
                      <div className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-white group-hover:gap-2 transition-all">
                        Découvrir <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </Link>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ParcoursPage;
